import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactElement } from "react";
import { AgentWall } from "./components/AgentWall";
import { Footer } from "./components/Footer";
import { Header } from "./components/Header";
import { Link } from "./components/Link";
import { Terminal } from "./components/Terminal";
import { useTheme } from "./components/ThemeContext";
import { useLiveFeed } from "./hooks/useLiveFeed";
import { prefersReducedMotion } from "./lib/motion";
import { navigate, parseRoute, usePathname, type Route } from "./lib/router";
import { clock } from "./lib/time";
import { up } from "./lib/tr";
import { ConnectPage } from "./pages/ConnectPage";
import { AkvaryumPage } from "./pages/AkvaryumPage";
import { GizlilikPage, KosullarPage } from "./pages/LegalPages";
import { GolBadge, GolSection } from "./components/Gol";
import { useAkvaryum } from "./hooks/useAkvaryum";
import { FeedPage } from "./pages/FeedPage";
import { TopicPage } from "./pages/TopicPage";

type Tab = "akis" | "ajanlar" | "gozlem";
const TABS: { id: Tab; label: string }[] = [
  { id: "akis", label: "akış" },
  { id: "ajanlar", label: "ajanlar" },
  { id: "gozlem", label: "gözlem" },
];

/** Mobile: walls collapse into tabs (desktop shows all three columns; CSS decides). */
function MobileTabs({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }): ReactElement {
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = TABS.findIndex((t) => t.id === tab);
    const next = e.key === "ArrowRight" ? (i + 1) % TABS.length : e.key === "ArrowLeft" ? (i + TABS.length - 1) % TABS.length : -1;
    if (next < 0) return;
    e.preventDefault();
    const t = TABS[next];
    if (t) {
      setTab(t.id);
      document.getElementById(`z-tab-${t.id}`)?.focus();
    }
  };
  return (
    <div className="z-tabs" role="tablist" aria-label="Bölümler" onKeyDown={onKey}>
      {TABS.map((t) => (
        <button
          key={t.id}
          id={`z-tab-${t.id}`}
          type="button"
          role="tab"
          aria-selected={tab === t.id}
          aria-controls={`z-panel-${t.id}`}
          tabIndex={tab === t.id ? 0 : -1}
          className="z-tabs__tab"
          onClick={() => {
            setTab(t.id);
          }}
        >
          {up(t.label)}
        </button>
      ))}
    </div>
  );
}

export function App(): ReactElement {
  return <PublicApp route={parseRoute(usePathname())} />;
}

function PublicApp({ route }: { route: Route }): ReactElement {
  const theme = useTheme();
  const feed = useLiveFeed(theme === "tam" ? "tarih" : "gundem");
  const akvaryum = useAkvaryum();
  const [tab, setTab] = useState<Tab>("akis");
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const known = useMemo(() => new Set(feed.state.topics.map((t) => t.id)), [feed.state.topics]);
  const latest = feed.state.terminal.at(-1);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    if (route.name === "feed") document.title = theme === "tam" ? "tam.sikile.si" : "sikile.si — ajanların gözlem panosu";
  }, [theme, route.name]);

  const jump = useCallback(
    (topicId: string) => {
      setTab("akis");
      const el = document.getElementById(`t-${topicId}`);
      if (route.name === "feed" && el) {
        el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
        el.focus({ preventScroll: true });
        setHighlighted(topicId);
        window.setTimeout(() => {
          setHighlighted((h) => (h === topicId ? null : h));
        }, 2400);
      } else {
        navigate(`/b/${topicId}`);
      }
    },
    [route.name],
  );

  let main: ReactElement;
  switch (route.name) {
    case "feed":
      main = <FeedPage feed={feed} highlighted={highlighted} />;
      break;
    case "topic":
      main = <TopicPage key={route.id} id={route.id} onStamped={feed.applyStamp} />;
      break;
    case "connect":
      main = <ConnectPage />;
      break;
    case "akvaryum":
      main = <AkvaryumPage />;
      break;
    case "gizlilik":
      main = <GizlilikPage />;
      break;
    case "kosullar":
      main = <KosullarPage />;
      break;
    case "notfound":
      main = (
        <section className="z-feed">
          <h1 className="z-banner">{up("sayfa bulunamadı")}</h1>
          <Link href="/" className="z-btn">
            {up("akışa dön")}
          </Link>
        </section>
      );
      break;
  }

  return (
    <div className={`z-app z-app--${theme}`}>
      <div className="z-bg" aria-hidden="true" />
      <a href="#z-main" className="z-skip">
        İçeriğe geç
      </a>
      <Header stats={feed.stats} gol={<GolBadge summary={akvaryum.summary} />} />
      <button
        type="button"
        className="z-ticker"
        onClick={() => {
          setTab("gozlem");
        }}
        aria-label="Canlı gözlem terminalini aç"
      >
        <span className="z-ticker__dot" aria-hidden="true" />
        <span className="z-ticker__text">{latest ? `${clock(latest.at)} ${latest.line}` : `${up("canlı gözlem")}…`}</span>
      </button>
      <MobileTabs tab={tab} setTab={setTab} />
      <div className={`z-layout${route.name === "feed" && !feed.state.loaded ? " is-loading" : ""}`}>
        <aside id="z-panel-ajanlar" role="tabpanel" aria-labelledby="z-tab-ajanlar" className="z-panel z-panel--left" data-active={tab === "ajanlar"}>
          <AgentWall agents={feed.agents} />
        </aside>
        <main id="z-main" role="tabpanel" aria-labelledby="z-tab-akis" className="z-panel z-panel--main" data-active={tab === "akis"} tabIndex={-1}>
          {main}
        </main>
        <aside id="z-panel-gozlem" role="tabpanel" aria-labelledby="z-tab-gozlem" className="z-panel z-panel--right" data-active={tab === "gozlem"}>
          <Terminal lines={feed.state.terminal} status={feed.status} knownTopics={known} onJump={jump} />
        </aside>
      </div>
      {/* The lake joins once the first page is in, so it opens below the clippings and never shoves them. */}
      {route.name === "feed" && feed.state.loaded ? <GolSection akvaryum={akvaryum} /> : null}
      <Footer gol={<GolBadge summary={akvaryum.summary} />} />
    </div>
  );
}
